import { useRouter } from "@tanstack/react-router";
import { createContext, createElement, useContext } from "react";

import type { AuthSession } from "@/integrations/auth/types";
import type { FeatureFlags } from "@/integrations/orpc/services/flags";

import { authClient } from "@/integrations/auth/client";
import { isSupabaseAuthProvider, type MultiResumeAuthProvider } from "@/utils/auth-provider";

type RootRouteContext = {
  flags?: FeatureFlags;
  session?: AuthSession | null;
};

const betterAuthSessionContext = createContext<AuthSession | null>(null);

function BetterAuthSessionProviderInner({ children }: { children: React.ReactNode }) {
  const { data } = authClient.useSession();

  return createElement(betterAuthSessionContext.Provider, { value: data as AuthSession | null }, children);
}

export function BetterAuthSessionProvider({ children, enabled }: { children: React.ReactNode; enabled: boolean }) {
  if (!enabled) {
    return createElement(betterAuthSessionContext.Provider, { value: null }, children);
  }

  return createElement(BetterAuthSessionProviderInner, null, children);
}

export function useAuthProvider(): MultiResumeAuthProvider {
  const router = useRouter();
  const context = router.state.matches[0]?.context as RootRouteContext | undefined;
  return context?.flags?.authProvider ?? "better-auth";
}

export function useAuthSession() {
  const router = useRouter();
  const authProvider = useAuthProvider();
  const context = router.state.matches[0]?.context as RootRouteContext | undefined;
  const betterAuthSession = useContext(betterAuthSessionContext);

  return isSupabaseAuthProvider(authProvider) ? (context?.session ?? null) : betterAuthSession;
}
